import EATable from "../../EATable";
import React, {useCallback, useEffect, useState, useMemo} from "react";
import {createCalculatedColumn, createColumn, createComponentColumn} from "../../../utils/ColumnUtils";
import {useHttp} from "../../../services/HttpService";
import EABetriebsakteMainContainer from "../EABetriebsakteMainContainer";
import {useHistory} from "react-router-dom";
import {ANLAGE} from "../../../consts/fachobjekt";
import EACombobox from "../../EACombobox";
import EAFieldset from "../../EAFieldset";
import {isEqual} from "lodash";
import {createCustomTextLink} from "../../../utils/RouteUtils";
import {StatusParameterMesswerte} from "../../../consts/valueLists";


export default function JahresberichtMesswerteUebersicht(props) {
    const history = useHistory();
    const [{
        data,
        loading
    }] = useHttp("/jahresbericht/betrieb/" + props.betriebId + "/parameter-messwert");
    const [filteredData, setFilteredData] = useState(undefined);

    const [saveActionLoading] = useState(false);

    function formatTimestamp(timestamp) {
        const date = new Date(timestamp);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    }

    function getStatusParamValue(statusID) {
        const status = StatusParameterMesswerte.find(item => item.id === statusID);
        return status ? status.value : statusID;
    }

    const transformedData = data?.map(item => {
        const newItem = {...item};
        for (let key in newItem) {
            if (newItem[key] && typeof newItem[key] === 'object' && newItem[key].hasOwnProperty('value')) {
                newItem[key] = newItem[key].value;
            }
        }
        if (item.grundlage) {
            newItem.grundlage = item.grundlage
        }
        if (item.parameterNr) {
            newItem.parameter = item.parameterNr.value;
        }
        if (item.haeufigkeit) {
            newItem.haeufigkeit = item.haeufigkeit;
        }
        if (item.probenahme) {
            newItem.probenahme = item.probenahme;
        }
        if (item.anforderungsParameterArt) {
            newItem.anforderungsParameterArt = item.anforderungsParameterArt;
        }
        if (item.gueltigVon) {
            newItem.gueltigVon = formatTimestamp(item.gueltigVon);
        }
        if (item.gueltigBis) {
            newItem.gueltigBis = formatTimestamp(item.gueltigBis);
        }
        if (item.wert) {
            newItem.wert = item.wert;
        }
        if (item.anzahlWerte) {
            newItem.anzahlWerte = item.anzahlWerte;
        }
        if (item.statistik) {
            newItem.statistik = item.statistik;
        }
        if(item.status) {
            newItem.status = getStatusParamValue(item.status);
        }
        return newItem;
    });

    const [selectedMessstellenName, setSelectedMessstellenName] = useState(null);
    const [selectedAnlageName, setSelectedAnlageName] = useState(null);
    const [selectedStatusMesswerte, setSelectedStatusMesswerte] = useState(null);
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [selectedAnforderungsparameter, setSelectedAnforderungsparameter] = useState(null);
    const [selectedStatistik, setSelectedStatistik] = useState(null);
    const [selectedProbenahme, setSelectedProbenahme] = useState(null);
    const [selectedBerichtsjahrVon, setSelectedBerichtsjahrVon] = useState(null);
    const [selectedBerichtsjahrBis, setSelectedBerichtsjahrBis] = useState(null);

    const berichtsjahre= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => new Date(item.jahresbericht.von).getFullYear().toString()))] : []
    ), [transformedData]);

    const anlagenNamen= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.jahresbericht.anlage.bezeichnung))] : []
    ), [transformedData]);

    const messstellenNamen= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.messstelle.bezeichnung))] : []
    ), [transformedData]);

    const statusMesswerte= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.status))] : []
    ), [transformedData]);

    const parameter= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.parameter))] : []
    ), [transformedData]);

    const anforderungsparameter= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.anforderungsParameterArt))] : []
    ), [transformedData]);

    const statistiken= useMemo(() => (
        transformedData ? [...new Set(transformedData.map(item => item.statistik))] : []
    ), [transformedData]);

    const probenahmen = useMemo(() => (
        transformedData ?
            [...new Set(transformedData.map(item => item.probenahme.value).filter(value => value !== null))] :
            []
    ), [transformedData]);

    const linkColumnComponentProviderAnlage = useCallback((row) => createCustomTextLink(row.jahresbericht.anlage.bezeichnung, ANLAGE, row.jahresbericht.anlage.id, history), [history]);

    const linkColumnComponentProviderParameter = useCallback((row) => {
        const currentPath = history.location.pathname;
        const basePathElements = currentPath.split("/uebersicht")[0];
        const linkStr = `${basePathElements}/${row.jahresbericht.id}/messstelle/${row.messstelle.id}/parameter/${row.id}`;

        return (
            <a href={linkStr} style={{ color: '#0288d1' }} onClick={(e) => {
                e.preventDefault();
                history.push(linkStr);
            }}>{row.parameter}</a>
        )
    }, [history]);

    const buildDefaultColumns = useCallback(() => {
        return [
            createCalculatedColumn(
                "berichtsjahr",
                (row) => (row?.jahresbericht?.von ? new Date(row.jahresbericht.von).getFullYear() : null),
                "Jahr"
            ),
            createComponentColumn("abwasseranlage", linkColumnComponentProviderAnlage, "Abwasseranlage"),
            createColumn("messstelle.bezeichnung", "Messstelle"),
            createColumn("status", "Status"),
            createComponentColumn("parameter", linkColumnComponentProviderParameter, "Parameter"),
            createColumn("anforderungsParameterArt", "Parameterart"),
            createColumn("statistik", "Statistik"),
            createColumn("probenahme.value", "Probenahmeart"),
            createColumn("wert", "Wert"),
            createColumn("einheit", "Einheit"),
        ];
    }, [linkColumnComponentProviderAnlage, linkColumnComponentProviderParameter]);

    useEffect(() => {
        if (transformedData && !loading) {
            let newData = [...transformedData];

            if (selectedMessstellenName != null) {
                newData = newData.filter(item => item.messstelle.bezeichnung === selectedMessstellenName);
            }
            if (selectedAnlageName != null) {
                newData = newData.filter(item => item.jahresbericht.anlage.bezeichnung === selectedAnlageName);
            }
            if (selectedStatusMesswerte != null) {
                newData = newData.filter(item => item.status === selectedStatusMesswerte);
            }
            if (selectedParameter != null) {
                newData = newData.filter(item => item.parameter === selectedParameter);
            }
            if (selectedAnforderungsparameter != null) {
                newData = newData.filter(item => item.anforderungsParameterArt === selectedAnforderungsparameter);
            }
            if (selectedStatistik != null) {
                newData = newData.filter(item => item.statistik === selectedStatistik);
            }
            if (selectedProbenahme != null) {
                newData = newData.filter(item => item.probenahme.value === selectedProbenahme);
            }
            if (selectedBerichtsjahrVon != null && selectedBerichtsjahrBis != null) {
                newData = newData.filter(item => {
                    const yearStart = new Date(item.jahresbericht.von).getFullYear();
                    const yearEnd = new Date(item.jahresbericht.bis).getFullYear();
                    return yearStart >= parseInt(selectedBerichtsjahrVon) && yearEnd <= parseInt(selectedBerichtsjahrBis)
                });
            }
            if (!isEqual(newData, filteredData)) {
                setFilteredData(newData);
            }
        }
    }, [transformedData, loading, selectedMessstellenName, filteredData, selectedAnforderungsparameter, selectedAnlageName, selectedBerichtsjahrVon, selectedBerichtsjahrBis, selectedParameter, selectedProbenahme, selectedStatistik, selectedStatusMesswerte]);

    return (
        <div className={"viewScrollContainer"}>
            <EAFieldset caption="Filter" style={{
                marginLeft: '10px',
                marginRight: '10px'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: '10px',
                }}>
                    <EACombobox
                        options={anlagenNamen}
                        label={"Anlagen-Name"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedAnlageName(newValue);
                        }}
                    />
                    <EACombobox
                        options={messstellenNamen}
                        label={"Messstellen-Name"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedMessstellenName(newValue);
                        }}
                    />
                    <EACombobox
                        options={statusMesswerte}
                        label={"Status Messwerte"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedStatusMesswerte(newValue);
                        }}
                    />
                    <EACombobox
                        options={parameter}
                        label={"Parameter"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedParameter(newValue);
                        }}
                    />
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}>
                    <EACombobox
                        options={anforderungsparameter}
                        label={"Anforderungsparameter"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedAnforderungsparameter(newValue);
                        }}
                    />
                    <EACombobox
                        options={statistiken}
                        label={"Statistik"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedStatistik(newValue);
                        }}
                    />
                    <EACombobox
                        options={probenahmen}
                        label={"Probenahme"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedProbenahme(newValue);
                        }}
                    />
                    <EACombobox
                        options={berichtsjahre}
                        label={"Berichtsjahr von"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedBerichtsjahrVon(newValue);
                        }}
                    />
                    <EACombobox
                        options={berichtsjahre}
                        label={"Berichtsjahr bis"}
                        style={{flex: 1, marginRight: "10px"}}
                        onChange={(event, newValue) => {
                            setSelectedBerichtsjahrBis(newValue);
                        }}
                    />
                </div>
            </EAFieldset>
            <EABetriebsakteMainContainer className={"tabsheet-content-margin"}>
                <EATable columns={buildDefaultColumns()} data={filteredData}
                         loading={loading || saveActionLoading || !transformedData}/>
            </EABetriebsakteMainContainer>
        </div>
    );
}